import type { Column } from "@/components/ui/data-table"
import { formatRelative } from "@/lib/format-date"
import { isClient, type Role } from "@/lib/roles"

import { CommsCell, FindingsCell, ProductCell, StatusCell } from "./queue-cells"
import type { QueueRow } from "./queue-data"

/**
 * The narrowest the queue table gets; below it the table scrolls sideways
 * instead. Chevron 28 + Product 260 + Status 132 + Findings 220 + Assignee 132
 * + Comms 96 + Updated 104, plus six 14px gaps and 32px of row padding ≈ 1090.
 * Keep it in step with the minimums below.
 */
export const QUEUE_TABLE_MIN_WIDTH = "min-w-[1100px]"

/**
 * Product reads left to right; every column after it is centred, header and
 * content alike. No column has a `priority`: on a narrow screen every column
 * stays and the table scrolls horizontally, rather than columns dropping out.
 *
 * A client gets no Assignee column (agreed with Yido): staff are "Review team" to a client, and
 * `/threads` tells a client no assignee, so the column would be a row of dashes. Everyone else
 * sees who holds each review. The expanded version rows follow these columns, so they drop it too.
 */
export function buildQueueColumns(role?: Role | null): Column<QueueRow>[] {
  const columns = allColumns()
  return isClient(role) ? columns.filter((c) => c.id !== "owner") : columns
}

function allColumns(): Column<QueueRow>[] {
  return [
    {
      id: "product",
      header: "Product",
      width: "minmax(260px,1.6fr)",
      cell: (row) => <ProductCell row={row} />,
    },
    {
      id: "status",
      header: "Status",
      width: "132px",
      align: "center",
      cell: (row) => <StatusCell status={row.status} stage={row.stage} />,
    },
    {
      id: "findings",
      header: "Findings",
      width: "minmax(220px,1fr)",
      align: "center",
      cell: (row) => <FindingsCell row={row} />,
    },
    {
      id: "owner",
      header: "Assignee",
      width: "132px",
      align: "center",
      cell: (row) =>
        row.assigneeName ? (
          <span className="min-w-0 truncate text-center text-body-sm text-text-2">
            {row.assigneeName}
          </span>
        ) : (
          <span className="text-body-sm text-text-3">—</span>
        ),
    },
    {
      id: "comms",
      header: "Comms",
      width: "96px",
      align: "center",
      cell: (row) => <CommsCell unread={row.commsUnread} />,
    },
    {
      id: "updatedAt",
      header: "Updated",
      width: "104px",
      align: "center",
      cell: (row) =>
        row.updatedAt ? (
          <span className="font-id tabular-nums text-body-sm text-text-3">
            {formatRelative(row.updatedAt)}
          </span>
        ) : (
          <span className="text-body-sm text-text-3">—</span>
        ),
    },
  ]
}
